import { combineReducers } from 'redux';

import * as types from 'modules/contactLists/actionTypes';
import { CONTACT_LIST_UPDATE_STATUS, DASHBOARD_SOURCE, META_SOURCE_UPLOAD } from 'modules/contactLists/constants';
import loading from 'modules/global/reducers/generic.loading';
import loadingArraySlice from 'modules/global/reducers/loadingArraySlice';
import createSearchReducers from 'modules/global/reducers/simpleSearchReducer';

import { createReducer } from 'utils/ReduxUtils';

import filters from './filters';
import initialized from './initialized';
import modal from './modal';
import selects from './selects';

/**
 * @typedef {{
 *  filters: import('modules/contactLists/reducers/ui/filters').ContactListsFiltersReducer,
 *  data: import('modules/global/reducers/simpleSearchReducer').SimpleSearchReducer,
 *  loading: {
 *    adding: boolean,
 *    export: boolean,
 *    create: boolean,
 *    delete: string[],
 *    importing: Record<string, boolean>,
 *  },
 *  modal: {
 *    creation: boolean,
 *  }
 * }} ContactListUIDashboardReducer
 */

/**
 * @typedef {{
 *  selects: Record<string, import('./selects.js').SliceSelect>,
 *  dashboard: ContactListUIDashboardReducer
 * }} ContactListsUIReducer
 */

/**
 * @param {import("modules/global/generic.types").Action<unknown>} action
 * @returns {boolean}
 */
const dashboardPageActionMatcher = ({ meta = {} }) => meta.source === DASHBOARD_SOURCE;

const GENERIC_SLICE_CONFIG = {
  global: {
    matcher: dashboardPageActionMatcher,
    selectId: entity => entity?.id ?? entity?.contactListId ?? entity,
  },
};

export default combineReducers({
  selects: selects(types.FETCH_CONTACT_LISTS),
  dashboard: combineReducers({
    filters,
    data: createSearchReducers(types.FETCH_CONTACT_LISTS, {
      global: { matcher: dashboardPageActionMatcher, paginationSlice: true },
      ids: {
        [types.CREATE_CONTACT_LIST_SUCCESS]: (state, { payload }) => [...state, payload.id],
      },
    }),
    loading: combineReducers({
      create: loading(types.CREATE_CONTACT_LIST),
      delete: loadingArraySlice(types.DELETE_CONTACT_LIST, GENERIC_SLICE_CONFIG),
    }),
    modal,
  }),
  details: combineReducers({
    initialized,
    adding: combineReducers({
      contactListId: createReducer(null, {
        [types.OPEN_ADD_CONTACTS_MODAL]: (_, { payload }) => payload.contactListId,
        [types.CLOSE_ADD_CONTACTS_MODAL]: () => null,
        [types.ADD_CONTACTS_SUCCESS]: () => null,
      }),
      mode: createReducer(null, {
        [types.OPEN_ADD_CONTACTS_MODAL]: (_, { payload }) => payload.mode,
        [types.CLOSE_ADD_CONTACTS_MODAL]: () => null,
        [types.ADD_CONTACTS_SUCCESS]: () => null,
      }),
    }),
    editing: combineReducers({
      contactListId: createReducer(null, {
        [types.OPEN_EXPORT]: (_, { payload: contactListId }) => contactListId,
        [types.CLOSE_EXPORT]: () => null,
        [types.EXPORT_CONTACT_LIST_SUCCESS]: () => null,
      }),
    }),
    loading: combineReducers({
      adding: loading(types.ADD_CONTACTS),
      export: loading(types.EXPORT_CONTACT_LIST),
      save: loading(types.UPDATE_CONTACT_LIST),
      delete: loading(types.DELETE_CONTACT_LIST),
      importing: createReducer({}, {
        [types.CLOSE_ADD_CONTACTS_MODAL]: (state, { payload: entityId, meta }) => ({
          ...state,
          ...(entityId ? { [entityId]: meta.source === META_SOURCE_UPLOAD } : {}),
        }),
        [types.WS_CONTACT_LIST_UPDATED]: (state, { payload }) => ({
          ...state,
          [payload.entityId]: payload.data?.type !== CONTACT_LIST_UPDATE_STATUS.PROCESSED,
        }),
        [types.FETCH_CONTACT_LIST_SUCCESS]: (state, { payload }) => ({
          ...state,
          [payload.id]: payload.status === CONTACT_LIST_UPDATE_STATUS.IMPORTING,
        }),
      }),
    }),
  }),
});
